/**
 * Microsoft Clarity helper functions
 * https://learn.microsoft.com/en-us/clarity/setup-and-installation/clarity-api
 */

// Declare the global clarity function
declare global {
  interface Window {
    clarity?: (...args: unknown[]) => void;
  }
}

/**
 * Identify a user session with custom data
 * @param userId - Unique user identifier (e.g., email or fingerprint)
 * @param customData - Optional custom hints object with user properties
 */
export function identifyUser(
  userId: string,
  customData?: { friendlyName?: string; email?: string }
): void {
  if (typeof window === 'undefined' || !window.clarity) {
    return;
  }

  try {
    // Identify the user - this associates the session with the user ID
    window.clarity('identify', userId);

    // Set custom session variables for name and email
    if (customData?.friendlyName) {
      window.clarity('set', 'name', customData.friendlyName);
    }
    if (customData?.email) {
      window.clarity('set', 'email', customData.email);
    }
  } catch (error) {
    console.error('Failed to identify user in Clarity:', error);
  }
}

/**
 * Set a custom tag/variable on the current Clarity session
 * @param key - Tag key
 * @param value - Tag value
 */
export function setClarityTag(key: string, value: string): void {
  if (typeof window === 'undefined' || !window.clarity) {
    return;
  }

  try {
    window.clarity('set', key, value);
  } catch (error) {
    console.error('Failed to set Clarity tag:', error);
  }
}

/**
 * Track a custom event in Clarity
 * @param eventName - Name of the event to track
 */
export function trackClarityEvent(eventName: string): void {
  if (typeof window === 'undefined' || !window.clarity) {
    return;
  }

  try {
    window.clarity('event', eventName);
  } catch (error) {
    console.error('Failed to track Clarity event:', error);
  }
}
